const SOURCE_CONTEXT_LINK_URL_KEYS = new Set([
  "externalsourceurl",
  "sourcelinkurl",
]);

const SOURCE_CONTEXT_ALLOWED_KEYS = new Set([
  ...SOURCE_CONTEXT_LINK_URL_KEYS,
  "agebusinessminutes",
  "amountcents",
  "attempts",
  "autocloseafterbusinessdays",
  "billingrecordtype",
  "businessblock",
  "businessclock",
  "category",
  "classification",
  "currency",
  "dueat",
  "errorcode",
  "errorname",
  "errortype",
  "eventtype",
  "executionmode",
  "evidencedueby",
  "evidenceprimary",
  "failurecode",
  // DEC-07: a repeated FHC report failure and the stage it last failed at.
  "failurestage",
  "hastemplaterecommendation",
  "inbox",
  "labelcount",
  "labelsummary",
  "lastmeaningfulactivityat",
  "matchedrecipientcount",
  "maxattempts",
  "occurrencecount",
  "outputchannel",
  "reason",
  "recipientstatus",
  "recommendedaction",
  "recoveryworkflow",
  "remediationpath",
  "reviewreason",
  "rulecatalog",
  "ruleid",
  "site",
  "sourcepage",
  "sourcepriority",
  "sourcesystem",
  "sourcestatus",
  "statuscategory",
  "stripeeventtype",
  "templatestatus",
  "ticketref",
  "unread",
  "workflowstep",
]);

const SOURCE_CONTEXT_UNSAFE_TEXT_PATTERN =
  /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|loginUrl|spoofToken|token=|bearer\s+[a-z0-9._-]+|https?:\/\/|stack trace|(?:SELECT|INSERT|UPDATE|DELETE)\s+/iu;

const HIDDEN_SOURCE_CONTEXT_KEYS = new Set([
  "actionpath",
  "alertid",
  "correlationid",
  "id",
  "organisationid",
  "seededby",
  "source",
  "sourcebadgelabel",
  "sourcelabel",
  "sourcelinklabel",
  "sourcerecordid",
  "summary",
  "title",
]);

const MAX_SOURCE_CONTEXT_TEXT_LENGTH = 160;

export function normaliseLiveAlertSourceMetadataKey(key: string): string {
  return key.replace(/[^a-z0-9]/giu, "").toLowerCase();
}

export const isLiveAlertSourceContextLinkUrlKey = (key: string): boolean =>
  SOURCE_CONTEXT_LINK_URL_KEYS.has(normaliseLiveAlertSourceMetadataKey(key));

export const isTrustedLiveAlertSourceContextUrl = (
  value: unknown,
): value is string => {
  if (typeof value !== "string" || value.trim().length === 0) {
    return false;
  }

  try {
    const url = new URL(value);

    return (
      url.protocol === "https:" &&
      (url.hostname === "hellocashflow-squad.monday.com" ||
        url.hostname === "monday.com" ||
        url.hostname.endsWith(".monday.com") ||
        url.hostname.endsWith(".slack.com"))
    );
  } catch {
    return false;
  }
};

export const normaliseTrustedLiveAlertSourceContextUrl = (
  value: string,
): string => {
  const url = new URL(value);
  url.search = "";
  url.hash = "";
  return url.toString();
};

export const isSafeLiveAlertSourceMetadataString = (
  key: string,
  value: string,
): boolean => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  if (isLiveAlertSourceContextLinkUrlKey(key)) {
    return isTrustedLiveAlertSourceContextUrl(trimmed);
  }

  return !SOURCE_CONTEXT_UNSAFE_TEXT_PATTERN.test(trimmed);
};

export const formatSafeLiveAlertSourceContextString = (
  value: string,
): string => {
  const trimmed = value.trim();
  return trimmed.length > MAX_SOURCE_CONTEXT_TEXT_LENGTH
    ? `${trimmed.slice(0, MAX_SOURCE_CONTEXT_TEXT_LENGTH - 1)}...`
    : trimmed;
};

const isDisplayableSourceContextValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return true;
  }

  if (Array.isArray(value)) {
    return (
      value.length > 0 &&
      value.every((item) =>
        ["string", "number", "boolean"].includes(typeof item),
      )
    );
  }

  return false;
};

export const getSafeLiveAlertSourceContextEntries = (
  metadata: Record<string, unknown>,
): Array<[string, unknown]> =>
  Object.entries(metadata)
    .filter(([key, value]) => {
      const normalisedKey = normaliseLiveAlertSourceMetadataKey(key);

      if (
        HIDDEN_SOURCE_CONTEXT_KEYS.has(normalisedKey) ||
        normalisedKey.endsWith("id") ||
        normalisedKey.includes("token") ||
        normalisedKey.includes("secret")
      ) {
        return false;
      }

      if (!SOURCE_CONTEXT_ALLOWED_KEYS.has(normalisedKey)) {
        return false;
      }

      if (typeof value === "string") {
        return isSafeLiveAlertSourceMetadataString(key, value);
      }

      if (Array.isArray(value)) {
        return value.every(
          (item) =>
            typeof item !== "string" ||
            isSafeLiveAlertSourceMetadataString(key, item),
        );
      }

      return isDisplayableSourceContextValue(value);
    })
    .slice(0, 8);
